/**
 * Unified patient home.
 *
 * One scrollable feed that assembles the user's full picture — greeting,
 * today's status, week trend, cycle context, and top patterns. The AI coach
 * lives behind a persistent FAB (PatientLayout), not a dashboard card.
 * Drill-downs (calendar, full pattern analytics) are linked from their preview
 * cards so nothing needs to live in a separate nav tab. Gating is deliberate:
 * cycle + patterns hide until data justifies them, so fresh users see a clean
 * on-ramp instead of a graveyard of empty cards.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { coachSeedForCondition } from '@pelvia/shared/lib/patient/coachSeed'
import InsightCard from '@/components/InsightCard'
import { captureEvent } from '@/lib/analytics'
import { conditionToLearnSlug, getLearnPage } from '@/pages/learn/learnPages'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useDashboard, type WeekLog } from '@pelvia/shared/hooks/patient/useDashboard'
import { useProfile } from '@pelvia/shared/hooks/patient/useProfile'
import { usePainPatterns, type PainPattern } from '@pelvia/shared/hooks/patient/usePainPatterns'
import { useCyclePredictor } from '@pelvia/shared/hooks/patient/useCyclePredictor'
import { CycleChart, PHASE_COLOR, DEFAULT_PHASE_LEGEND } from './CycleChart'
import { usePainForecast, type ForecastDay } from '@pelvia/shared/hooks/patient/usePainForecast'
import { useConditionFlags, type ConditionFlag } from '@pelvia/shared/hooks/patient/useConditionFlags'
import {
  useWearableStatus,
  useConnectWearable,
  useWearableInsights,
  type WearableInsightsResponse,
  type WearableSeriesPoint,
} from '@pelvia/shared/hooks/patient/useWearables'
import { painColor, painTextColor } from '@pelvia/shared/lib/patient/painColors'
import CalendarSection from './CalendarSection'
import SetupChecklistCard from './SetupChecklistCard'
import MetricsCard from './pattern-cards/MetricsCard'
import TriggersCard from './pattern-cards/TriggersCard'
import WeeklyTrendCard from './pattern-cards/WeeklyTrendCard'
import SymptomFrequencyCard from './pattern-cards/SymptomFrequencyCard'
import SleepCard from './pattern-cards/SleepCard'
import CyclePhaseCard from './pattern-cards/CyclePhaseCard'
import TomorrowAlertCard from './pattern-cards/TomorrowAlertCard'
import ReportCard from './pattern-cards/ReportCard'

const LEARN_CARD_DISMISSED_KEY = 'pelvia_learn_card_dismissed'
const WEARABLE_BANNER_DISMISSED_KEY = 'pelvia_wearable_banner_dismissed'
const WEARABLE_BANNER_VIEWS_KEY = 'pelvia_wearable_banner_views'
// Auto-hide the nudge after this many page views even if never dismissed, so
// users without a wearable aren't shown it forever.
const WEARABLE_BANNER_MAX_VIEWS = 4

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
function useRevealOnScroll<T extends HTMLElement>() {
  const ref = useRef<T | null>(null)
  const [revealed, setRevealed] = useState(false)
  useEffect(() => {
    const node = ref.current
    if (!node || revealed) return
    if (typeof IntersectionObserver === 'undefined') {
      setRevealed(true)
      return
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setRevealed(true)
            io.disconnect()
            break
          }
        }
      },
      { threshold: 0.18, rootMargin: '0px 0px -40px 0px' },
    )
    io.observe(node)
    return () => io.disconnect()
  }, [revealed])
  return { ref, revealed }
}

function timeOfDayGreeting(): string {
  const hour = new Date().getHours()
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  return 'Good evening'
}

export default function BriefingPage() {
  const { data: user } = useCurrentUser()
  const { data: profileResp } = useProfile()
  const { data: dashboard, isLoading: dashLoading } = useDashboard()

  const streak = dashboard?.streak?.current_streak ?? 0
  const weekLogs = dashboard?.week_logs ?? []
  const weekAvg = dashboard?.week_avg
  const predicted = dashboard?.predicted_score
  const todayPain = dashboard?.today_pain
  const loggedToday = !!todayPain
  // Total distinct days the user has ever logged pain on. Drives gating of
  // cycle/pattern/forecast cards — once a user crosses the threshold the
  // unlock sticks, even on weeks they log less. Counting trailing-week
  // activity instead would re-lock returning users on quiet weeks.
  const daysLogged = dashboard?.total_days_logged ?? 0

  // The "Your patterns" insight UI unlocks at 7 days, but we fetch the
  // pain-patterns payload from 3 days so the analytics cards moved off the old
  // pattern page (metrics, trend, triggers, …) can fade in as data accrues —
  // each card self-hides until its own data guard is met.
  const painDataEnabled = daysLogged >= 3
  const patternsEnabled = daysLogged >= 7
  const cycleEnabled = daysLogged >= 3
  // Forecast needs ≥5 real logs to avoid fantasy predictions on fresh accounts.
  const forecastEnabled = daysLogged >= 5
  const { data: patternsData, isLoading: patternsLoading } = usePainPatterns(painDataEnabled)
  const { data: cycleData, isLoading: cycleLoading } = useCyclePredictor(cycleEnabled)
  const { data: flagsData } = useConditionFlags()
  const { data: forecastData, isLoading: forecastLoading } = usePainForecast(forecastEnabled)
  const { data: wearableInsights } = useWearableInsights()

  // Settings' display_name is the user's chosen greeting name; fall back to
  // their sign-up full name. This keeps the greeting in sync after Settings edits.
  const displayName = profileResp?.profile?.display_name?.trim() || user?.full_name || ''
  const firstName = displayName.split(' ')[0] ?? ''
  const greeting = timeOfDayGreeting()

  return (
    <div>
      <SetupChecklistCard />

      <HeroGreeting
        greeting={greeting}
        firstName={firstName}
        weekAvg={weekAvg}
        predicted={predicted}
        streak={streak}
        hasData={daysLogged > 0}
      />

      {/* PelviaCare+ — promoted to the top of the feed. Members see their
          consult-note status; everyone else sees the $300 enticement explaining
          what they get. It's the primary membership/upgrade surface. */}
      <ReportCard />

      {/* High-visibility heads-up when tomorrow's pain is predicted high. */}
      <TomorrowAlertCard alert={patternsData?.tomorrow_alert} />

      {/* 30-day adherence + pain map. Sits at the top, just below the hero,
          since the calendar is the primary anchor users return to. */}
      <CalendarSection />

      <WearableSkipperBanner />

      <TodayCard todayPain={todayPain} loggedToday={loggedToday} />

      <ConditionLearnCard conditions={profileResp?.profile?.conditions ?? []} />

      {wearableInsights?.connected && wearableInsights.last_night && (
        <WearableInsightsCard data={wearableInsights} />
      )}

      <WeekCard logs={weekLogs} loading={dashLoading} />

      <MetricsCard metrics={patternsData?.metrics} />

      {/* Your patterns — promoted high on the page now that each insight is
          quantified and actionable (correlations, biometrics, cycle, meds). */}
      {patternsEnabled && patternsLoading && !patternsData ? (
        <PatternsCardSkeleton />
      ) : (
        <PatternsCard
          patterns={patternsData?.patterns_detected ?? []}
          unlocked={patternsEnabled}
          daysLogged={daysLogged}
        />
      )}

      <TriggersCard triggers={patternsData?.triggers} />

      <WeeklyTrendCard trend={patternsData?.pain_trend} />

      <SymptomFrequencyCard frequency={patternsData?.symptom_frequency} />

      <SleepCard sleep={patternsData?.sleep_pain} />

      <CyclePhaseCard cyclePhase={patternsData?.cycle_phase_pain} />

      {forecastEnabled && forecastLoading && !forecastData && <ForecastCardSkeleton />}
      {forecastEnabled && forecastData?.forecast && forecastData.forecast.length > 0 && (
        <ForecastCard
          forecast={forecastData.forecast}
          llmUsed={!!forecastData.llm_used}
        />
      )}

      <ConditionFlagsCard flags={flagsData?.flags ?? []} />

      {cycleEnabled && cycleLoading && !cycleData && <CycleCardSkeleton />}
      {cycleEnabled && cycleData?.data_quality === 'paused' && (
        <section className="card cycle-predictor-card">
          <div className="card-header">
            <span className="card-title">Cycle Predictor</span>
            <span className="cycle-phase-pill">Paused</span>
          </div>
          <p className="cycle-paused-message">
            {cycleData.message ??
              'Continuous hormonal therapy suppresses ovulation and withdrawal bleeds. Symptom tracking continues normally.'}
          </p>
        </section>
      )}
      {cycleEnabled && cycleData?.data_quality === 'insufficient' && (
        <section className="card cycle-predictor-card">
          <div className="card-header">
            <span className="card-title">Cycle Predictor</span>
          </div>
          <p className="cycle-paused-message">
            {cycleData.message ??
              'Log your period flow in the daily check-in to unlock cycle predictions.'}
          </p>
          <Link to="/patient/profile#cycle-history" className="cycle-nudge-cta">
            Add your recent periods →
          </Link>
        </section>
      )}
      {cycleEnabled && cycleData?.data_quality !== 'insufficient' && cycleData?.metrics && (
        <CycleCard data={cycleData} />
      )}
    </div>
  )
}

// ----- Hero -----

function HeroGreeting({
  greeting,
  firstName,
  weekAvg,
  predicted,
  streak,
  hasData,
}: {
  greeting: string
  firstName: string
  weekAvg: number | null | undefined
  predicted: number | null | undefined
  streak: number
  hasData: boolean
}) {
  const subtitle = useMemo(() => {
    if (!hasData) {
      return "Log today to start building your picture. Small, consistent logs unlock real patterns."
    }
    if (weekAvg == null) {
      return "Every log you save builds your picture. Today counts."
    }
    const parts: Array<React.ReactNode> = [
      'Your week averaged ',
      <strong key="avg">{weekAvg}</strong>,
      '.',
    ]
    if (predicted != null) {
      parts.push(' Today looks like a ', <strong key="pred">{predicted}</strong>, '.')
    }
    parts.push(' Every log builds the picture.')
    return parts
  }, [hasData, weekAvg, predicted])

  return (
    <div className="hero-greeting">
      <div className="hero-greeting-row">
        <h1>
          {greeting}
          {firstName ? (<>, <em>{firstName}</em></>) : null}.
        </h1>
        {streak > 0 && <span className="streak-pill">{streak} day streak</span>}
      </div>
      <p className="hero-subtitle">{subtitle}</p>
    </div>
  )
}

// ----- Today card (flips between CTA and summary) -----

function ConditionLearnCard({ conditions }: { conditions: string[] }) {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(LEARN_CARD_DISMISSED_KEY) === '1' } catch { return false }
  })

  const slug =
    conditions.includes('endometriosis') ? 'endometriosis'
    : conditions.includes('adenomyosis') ? 'adenomyosis'
    : conditions.some((c) => conditionToLearnSlug(c) === 'treatment') ? 'treatment'
    : null

  const page = slug ? getLearnPage(slug) : null

  if (!page || dismissed) return null

  function dismiss() {
    try { localStorage.setItem(LEARN_CARD_DISMISSED_KEY, '1') } catch { /* quota */ }
    setDismissed(true)
  }

  return (
    <section className="card learn-nudge-card">
      <div className="learn-nudge-header">
        <span className="learn-nudge-eyebrow">Recommended read</span>
        <button type="button" className="learn-nudge-dismiss" onClick={dismiss} aria-label="Dismiss">×</button>
      </div>
      <a
        href={page.path}
        target="_blank"
        rel="noopener noreferrer"
        className="learn-nudge-link"
        onClick={() => {
          captureEvent('learn_page_viewed', { slug: page.slug, source: 'briefing_nudge' })
          dismiss()
        }}
      >
        <strong className="learn-nudge-title">{page.title}</strong>
        <p className="learn-nudge-body">{page.oneLiner}</p>
        <span className="learn-nudge-cta">Read →</span>
      </a>
    </section>
  )
}

function TodayCard({
  todayPain,
  loggedToday,
}: {
  todayPain: { pain_score: number; notes?: string | null } | null | undefined
  loggedToday: boolean
}) {
  if (loggedToday && todayPain) {
    const score = todayPain.pain_score
    return (
      <section className="card today-card today-card-done">
        <div className="today-done-row">
          <div
            className="today-done-score"
            style={{ background: painColor(score), color: painTextColor(score) }}
          >
            {score}
          </div>
          <div className="today-done-body">
            <strong>Today's check-in complete</strong>
            <div className="today-done-sub">
              {score <= 3
                ? 'A gentle day. Nice work tuning in.'
                : score <= 6
                ? 'Mid-range day logged. Keep going — every log helps.'
                : 'Rough day logged. Be kind to yourself today.'}
            </div>
          </div>
          <Link to="/patient/checkin" className="today-done-edit">Edit</Link>
        </div>
      </section>
    )
  }
  return (
    <section className="card today-card today-card-cta">
      <div className="today-cta-copy">
        <strong>Daily check-in</strong>
        <p>Takes about two minutes. Your data powers your insights.</p>
      </div>
      <Link to="/patient/checkin" className="btn btn-primary today-cta-btn">
        Start today's check-in
      </Link>
    </section>
  )
}

// ----- Week card -----

function WeekCard({ logs, loading }: { logs: WeekLog[]; loading: boolean }) {
  const [showEstimates, setShowEstimates] = useState(true)
  const [infoOpen, setInfoOpen] = useState(false)
  const hasEstimates = logs.some((l) => l.source === 'oura_estimated')
  return (
    <section className="card">
      <div className="card-header">
        <span className="card-title">This Week</span>
      </div>
      <WeekChart logs={logs} loading={loading} showEstimates={showEstimates} />
      {hasEstimates && (
        <div className="chart-legend">
          <span className="chart-legend-item">
            <span className="chart-legend-swatch chart-legend-solid" /> Logged
          </span>
          <span className="chart-legend-item">
            <span className="chart-legend-swatch chart-legend-dashed" /> Estimated
          </span>
          <label className="chart-legend-toggle">
            <input
              type="checkbox"
              checked={showEstimates}
              onChange={(e) => setShowEstimates(e.target.checked)}
            />
            Show estimates
          </label>
          <button
            type="button"
            className="chart-legend-info"
            onClick={() => setInfoOpen(true)}
            aria-label="How are estimates computed?"
          >
            ?
          </button>
        </div>
      )}
      {infoOpen && <EstimateInfoModal onClose={() => setInfoOpen(false)} />}
    </section>
  )
}

function WeekChart({
  logs,
  loading,
  showEstimates = true,
}: {
  logs: WeekLog[]
  loading: boolean
  showEstimates?: boolean
}) {
  // Build last 7 days (oldest → newest) regardless of loading — skeleton and
  // chart share the same 7-slot layout so the line doesn't jump on arrival.
  const today = new Date()
  const cols: {
    date: Date
    score: number | null
    zeroFilled: boolean
    estimated: boolean
    confidence: number | null
  }[] = []
  for (let i = 6; i >= 0; i--) {
    const d = new Date(today)
    d.setDate(today.getDate() - i)
    const iso = d.toLocaleDateString('en-CA')
    const log = logs.find((l) => l.log_date === iso)
    const estimated = log?.source === 'oura_estimated'
    const hideEstimate = estimated && !showEstimates
    cols.push({
      date: d,
      score: hideEstimate ? null : log?.pain_score ?? null,
      zeroFilled: !!log?.zero_filled || hideEstimate,
      estimated,
      confidence: log?.confidence ?? null,
    })
  }

  // SVG viewBox — a responsive line inside a fixed aspect frame. Padding
  // leaves room for the top marker ring and bottom day labels.
  const W = 280
  const H = 96
  const PAD_X = 14
  const PAD_TOP = 10
  const PAD_BOTTOM = 10
  const plotW = W - PAD_X * 2
  const plotH = H - PAD_TOP - PAD_BOTTOM

  const xAt = (i: number) => PAD_X + (i / (cols.length - 1)) * plotW
  const yAt = (score: number) => PAD_TOP + plotH - (score / 10) * plotH

  // Real points only (skip missing days). Connecting across gaps is honest
  // enough for a "how was your week" trend and keeps the curve continuous.
  const points = cols
    .map((c, i) => ({
      i,
      x: xAt(i),
      y: c.score == null || c.zeroFilled ? null : yAt(c.score),
      score: c.score,
      zeroFilled: c.zeroFilled,
      estimated: c.estimated,
      confidence: c.confidence,
    }))
    .filter((p) => p.y != null) as {
      i: number
      x: number
      y: number
      score: number
      zeroFilled: boolean
      estimated: boolean
      confidence: number | null
    }[]

  const pathD = buildSmoothPath(points.map((p) => ({ x: p.x, y: p.y })))
  const gradientId = 'week-pain-grad'

  return (
    <div className="week-chart" aria-busy={loading}>
      <svg
        className="week-chart-svg"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        role="img"
        aria-label="Pain scores for the last 7 days"
      >
        <defs>
          <linearGradient id={gradientId} x1="0" x2="1" y1="0" y2="0">
            {points.map((p, idx) => (
              <stop
                key={idx}
                offset={`${((p.x - PAD_X) / plotW) * 100}%`}
                stopColor={painColor(p.score)}
              />
            ))}
          </linearGradient>
        </defs>

        {/* Faint horizontal grid at pain=5 as a reference line */}
        <line
          x1={PAD_X}
          x2={W - PAD_X}
          y1={yAt(5)}
          y2={yAt(5)}
          stroke="var(--border)"
          strokeDasharray="2 4"
          strokeWidth="1"
          opacity="0.6"
        />

        {pathD && !loading && (
          <>
            {/* Soft area under the curve for presence */}
            <path
              d={`${pathD} L ${points[points.length - 1].x} ${PAD_TOP + plotH} L ${points[0].x} ${PAD_TOP + plotH} Z`}
              fill={`url(#${gradientId})`}
              opacity="0.14"
            />
            <path
              className="week-chart-line"
              d={pathD}
              fill="none"
              stroke={`url(#${gradientId})`}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
            {points.map((p, idx) => (
              <g
                key={p.i}
                className="week-chart-dot"
                style={{
                  animationDelay: `${300 + idx * 60}ms`,
                  ['--dot-opacity' as any]: p.estimated ? 0.7 : 1,
                }}
              >
                <circle
                  cx={p.x}
                  cy={p.y}
                  r="5"
                  fill={painColor(p.score)}
                  stroke="var(--surface)"
                  strokeWidth="1.5"
                  strokeDasharray={p.estimated ? '2 2' : undefined}
                  vectorEffect="non-scaling-stroke"
                />
                <text
                  x={p.x}
                  y={p.y}
                  className="week-chart-dot-label"
                  textAnchor="middle"
                  dominantBaseline="central"
                >
                  {p.score}
                </text>
                <title>
                  {p.estimated
                    ? `~${p.score} (estimated${p.confidence != null ? `, confidence ${Math.round(p.confidence * 100)}%` : ''})`
                    : `Pain ${p.score}`}
                </title>
              </g>
            ))}
          </>
        )}

        {/* Missing-day hollow markers sit on the baseline for honesty */}
        {!loading &&
          cols.map((c, i) =>
            c.score == null || c.zeroFilled ? (
              <circle
                key={`miss-${i}`}
                cx={xAt(i)}
                cy={PAD_TOP + plotH}
                r="2"
                fill="var(--border)"
              >
                <title>Not logged</title>
              </circle>
            ) : null,
          )}
      </svg>
      <div className="week-chart-labels">
        {cols.map((c) => (
          <span key={c.date.toISOString()} className="week-bar-label">
            {DAY_LABELS[c.date.getDay()]}
          </span>
        ))}
      </div>
    </div>
  )
}

// Catmull-Rom → cubic Bezier. Produces a smooth curve through every data
// point without requiring a full spline library.
function buildSmoothPath(pts: { x: number; y: number }[]): string {
  if (pts.length === 0) return ''
  if (pts.length === 1) return `M ${pts[0].x} ${pts[0].y}`
  const tension = 0.5
  let d = `M ${pts[0].x} ${pts[0].y}`
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] ?? pts[i]
    const p1 = pts[i]
    const p2 = pts[i + 1]
    const p3 = pts[i + 2] ?? p2
    const cp1x = p1.x + ((p2.x - p0.x) / 6) * tension * 2
    const cp1y = p1.y + ((p2.y - p0.y) / 6) * tension * 2
    const cp2x = p2.x - ((p3.x - p1.x) / 6) * tension * 2
    const cp2y = p2.y - ((p3.y - p1.y) / 6) * tension * 2
    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`
  }
  return d
}

// ----- Cycle card -----
// Full predictor card: current phase pill, metrics grid, 30-day predicted
// pain chart with min/max range, and up to 3 contextual insights. Chart bars
// use pixel heights (not %) so values scale honestly — a 3/10 really is half
// the height of a 6/10.

function CycleCard({
  data,
}: {
  data: NonNullable<ReturnType<typeof useCyclePredictor>['data']>
}) {
  const { metrics, chart, insights, phases } = data
  if (!metrics) return null
  const color = PHASE_COLOR[metrics.current_phase] ?? 'var(--accent)'
  const legend = phases ?? DEFAULT_PHASE_LEGEND

  const variability = metrics.cycle_variability
  const isWithdrawal = metrics.next_period_kind === 'withdrawal_bleed'
  const showRange =
    variability?.band === 'irregular' || variability?.band === 'highly_irregular'
  const range = metrics.next_period_range
  const nextValue =
    showRange && range
      ? `${range.start_label}–${range.end_label}`
      : (metrics.next_period_label ?? '—')

  const periodCard = metrics.period_in_progress
    ? {
        label: 'Period',
        value: `Day ${metrics.cycle_day_today}`,
        sub: metrics.next_period_label
          ? `Next predicted ${metrics.next_period_label}`
          : undefined,
        accent: true,
      }
    : {
        // Under cyclic hormonal therapy this is a scheduled withdrawal bleed.
        label: isWithdrawal ? 'Est. withdrawal bleed' : 'Period starts',
        value: nextValue,
        sub:
          metrics.days_until_period > 0
            ? `${showRange ? 'around ' : 'in '}${metrics.days_until_period} days`
            : 'expected today',
      }
  const ovSuppressed = metrics.hormonal_therapy?.ovulation_suppressed
  const ov = metrics.ovulation_window
  const ovSub =
    metrics.prediction_source === 'temp'
      ? 'temperature confirmed'
      : metrics.prediction_source === 'lh'
        ? 'LH surge confirmed'
        : metrics.prediction_source === 'mucus'
          ? 'based on mucus data'
          : metrics.prediction_source === 'symptom'
            ? 'based on symptoms'
            : ov?.peak
              ? `peak ${ov.peak}`
              : undefined
  const ovCopy = ov ? `${ov.start}–${ov.end}` : '—'
  const fertile = metrics.fertile_window
  const confLabel =
    metrics.ovulation_confidence === 'high'
      ? 'high confidence'
      : metrics.ovulation_confidence === 'moderate'
        ? 'moderate confidence'
        : 'estimated'
  const ovulationCard = ovSuppressed
    ? { label: 'Ovulation window', value: 'Suppressed', sub: 'by hormonal therapy' }
    : { label: 'Ovulation window', value: ovCopy, sub: ovSub }
  const metricItems: { label: string; value: string; sub?: string; accent?: boolean }[] = [
    periodCard,
    ovulationCard,
    // Fertile-window tile is suppressed by the backend when neither
    // mucus-based nor Standard-Days-Method (26-32 day cycles) prediction
    // applies — showing a window outside SDM's validated range would be
    // misleading. Skip the tile entirely in that case.
    ...(fertile
      ? [{
          label: 'Fertile window',
          value: `${fertile.start}–${fertile.end}`,
          sub: confLabel,
        }]
      : []),
    {
      label: 'Cycle day',
      value: `Day ${metrics.cycle_day_today}`,
      sub: metrics.phase_label,
    },
  ]

  return (
    <section className="card cycle-predictor-card">
      <div className="card-header">
        <span className="card-title">Cycle Predictor</span>
        <span
          className="cycle-phase-pill"
          style={{ background: `${color}22`, color }}
        >
          {metrics.phase_label}
        </span>
      </div>
      {(metrics.anovulatory_suspected || variability?.band === 'highly_irregular') && (
        <div className="cycle-review-note" style={{ color: PHASE_COLOR.luteal }}>
          {metrics.anovulatory_reason
            ? `${metrics.anovulatory_reason}. `
            : 'Your cycles are highly variable. '}
          Predictions show a range — consider discussing with your provider.
        </div>
      )}
      <div className="cycle-metrics">
        {metricItems.map((m) => (
          <div
            key={m.label}
            className={`cycle-metric-card${m.accent ? ' cycle-metric-accent' : ''}`}
          >
            <div className="cycle-metric-label">{m.label}</div>
            <div className="cycle-metric-value">{m.value}</div>
            {m.sub && <div className="cycle-metric-sub">{m.sub}</div>}
          </div>
        ))}
      </div>
      <div className="cycle-legend">
        {legend.map((p) => (
          <span key={p.name} className="cycle-legend-item">
            <span className="cycle-legend-dot" style={{ background: p.color }} />
            {p.name}
          </span>
        ))}
      </div>
      {chart && chart.length > 0 && <CycleChart chart={chart} />}
      {insights && insights.length > 0 && (
        <CycleInsightsGrid insights={insights} />
      )}
      {/* Low-confidence nudge: the surest way for the user to sharpen the
          prediction is to log more period history. */}
      {metrics.ovulation_confidence === 'low' && (
        <Link to="/patient/profile#cycle-history" className="cycle-nudge-cta">
          Low confidence — add your recent periods to improve it →
        </Link>
      )}
    </section>
  )
}

// ----- Forecast card -----
// Forward-looking 5-day pain outlook. The model is a weighted moving average
// nudged by day-of-week, cycle phase, and med adherence, with an LLM smoothing
// pass bounded to ±1.2 so the rationale can add colour without changing the
// shape.

function ForecastCard({
  forecast,
  llmUsed,
}: Readonly<{ forecast: ForecastDay[]; llmUsed: boolean }>) {
  const { ref, revealed } = useRevealOnScroll<HTMLElement>()
  const W = 280
  const H = 100
  const PAD_X = 14
  const PAD_TOP = 12
  const PAD_BOTTOM = 10
  const plotW = W - PAD_X * 2
  const plotH = H - PAD_TOP - PAD_BOTTOM

  const pts = forecast.map((d, i) => {
    const x = PAD_X + (i / Math.max(1, forecast.length - 1)) * plotW
    const y = PAD_TOP + plotH - (d.predicted_score / 10) * plotH
    return { ...d, x, y, i }
  })
  const pathD = buildSmoothPath(pts.map((p) => ({ x: p.x, y: p.y })))
  // Extend the dashed forecast line slightly past the first/last dots so the
  // trend doesn't look like it snaps to a stop at the edge dots.
  const EXT = 7
  const first = pts[0]
  const last = pts[pts.length - 1]
  const second = pts[1] ?? first
  const secondLast = pts[pts.length - 2] ?? last
  const angleStart = Math.atan2(second.y - first.y, second.x - first.x)
  const angleEnd = Math.atan2(last.y - secondLast.y, last.x - secondLast.x)
  const preX = first.x - Math.cos(angleStart) * EXT
  const preY = first.y - Math.sin(angleStart) * EXT
  const postX = last.x + Math.cos(angleEnd) * EXT
  const postY = last.y + Math.sin(angleEnd) * EXT
  const avg =
    forecast.reduce((s, d) => s + d.predicted_score, 0) / forecast.length
  const peak = forecast.reduce(
    (best, d) => (d.predicted_score > best.predicted_score ? d : best),
    forecast[0],
  )

  return (
    <section
      className={`card forecast-card${revealed ? ' is-revealed' : ''}`}
      ref={ref}
    >
      <div className="card-header">
        <span className="card-title">Next 5 days outlook</span>
        {llmUsed && <span className="forecast-pill">AI smoothed</span>}
      </div>
      <div className="forecast-summary">
        <div className="forecast-summary-item">
          <span className="forecast-summary-label">Average</span>
          <span className="forecast-summary-value" style={{ color: painColor(avg) }}>
            {avg.toFixed(1)}
          </span>
        </div>
        <div className="forecast-summary-item">
          <span className="forecast-summary-label">Toughest day</span>
          <span className="forecast-summary-value" style={{ color: painColor(peak.predicted_score) }}>
            {forecastDayLabel(peak.date)} · {peak.predicted_score.toFixed(1)}
          </span>
        </div>
      </div>
      <div className="forecast-chart">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="xMidYMid meet"
          className="forecast-svg"
          role="img"
          aria-label="Predicted pain for the next 5 days"
        >
          <defs>
            <linearGradient id="forecast-grad" x1="0" x2="1" y1="0" y2="0">
              {pts.map((p, idx) => (
                <stop
                  key={idx}
                  offset={`${((p.x - PAD_X) / plotW) * 100}%`}
                  stopColor={painColor(p.predicted_score)}
                />
              ))}
            </linearGradient>
            <linearGradient id="forecast-fill" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="var(--accent, #6e3bff)" stopOpacity="0.18" />
              <stop offset="100%" stopColor="var(--accent, #6e3bff)" stopOpacity="0" />
            </linearGradient>
          </defs>
          <line
            x1={PAD_X}
            x2={W - PAD_X}
            y1={PAD_TOP + plotH - (5 / 10) * plotH}
            y2={PAD_TOP + plotH - (5 / 10) * plotH}
            stroke="var(--border)"
            strokeDasharray="2 4"
            strokeWidth="1"
            opacity="0.6"
          />
          {pathD && (
            <>
              <path
                d={`${pathD} L ${pts[pts.length - 1].x} ${PAD_TOP + plotH} L ${pts[0].x} ${PAD_TOP + plotH} Z`}
                fill="url(#forecast-fill)"
                className="forecast-area"
              />
              <line
                x1={preX}
                y1={preY}
                x2={first.x}
                y2={first.y}
                stroke={painColor(first.predicted_score)}
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeDasharray="5 4"
                vectorEffect="non-scaling-stroke"
                opacity="0.55"
                className="forecast-line-ext"
              />
              <line
                x1={last.x}
                y1={last.y}
                x2={postX}
                y2={postY}
                stroke={painColor(last.predicted_score)}
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeDasharray="5 4"
                vectorEffect="non-scaling-stroke"
                opacity="0.55"
                className="forecast-line-ext"
              />
              <path
                d={pathD}
                fill="none"
                stroke="url(#forecast-grad)"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="5 4"
                vectorEffect="non-scaling-stroke"
                className="forecast-line"
              />
              {pts.map((p, idx) => (
                <g
                  key={p.date}
                  className="forecast-dot"
                  style={{ animationDelay: `${260 + idx * 80}ms` }}
                >
                  <circle
                    cx={p.x}
                    cy={p.y}
                    r="4.2"
                    fill={painColor(p.predicted_score)}
                    stroke="var(--surface)"
                    strokeWidth="1.5"
                    vectorEffect="non-scaling-stroke"
                  />
                  <text
                    x={p.x}
                    y={p.y - 8}
                    className="forecast-dot-label"
                    textAnchor="middle"
                    dominantBaseline="middle"
                  >
                    {p.predicted_score.toFixed(1)}
                  </text>
                  <title>
                    {`${forecastDayLabel(p.date)}: ${p.predicted_score.toFixed(1)}/10`}
                    {p.rationale ? ` — ${p.rationale}` : ''}
                  </title>
                </g>
              ))}
            </>
          )}
        </svg>
        <div className="forecast-labels">
          {pts.map((p) => (
            <span key={p.date} className="forecast-label">
              {forecastDayLabel(p.date)}
            </span>
          ))}
        </div>
      </div>
      {forecast.some((d) => d.rationale) && (
        <ul className="forecast-rationale-list">
          {forecast
            .filter((d) => d.rationale)
            .slice(0, 3)
            .map((d, idx) => (
              <li
                key={d.date}
                className="forecast-rationale-item"
                style={{ ['--forecast-rationale-delay' as string]: `${idx * 80}ms` }}
              >
                <span
                  className="forecast-rationale-dot"
                  style={{ background: painColor(d.predicted_score) }}
                />
                <span className="forecast-rationale-day">
                  {forecastDayLabel(d.date)}
                </span>
                <span className="forecast-rationale-body">{d.rationale}</span>
              </li>
            ))}
        </ul>
      )}
      <div className="forecast-disclaimer">
        Projection only — based on your recent patterns and cycle. Today's logs
        refine tomorrow's forecast.
      </div>
    </section>
  )
}

function forecastDayLabel(iso: string): string {
  const d = new Date(iso + 'T12:00:00')
  return d.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' })
}

function ForecastCardSkeleton() {
  return (
    <section className="card forecast-card forecast-card-skeleton" aria-busy="true" aria-label="Loading next 5 days outlook">
      <div className="card-header">
        <span className="skeleton-block skeleton-title" />
      </div>
      <div className="forecast-summary">
        <div className="forecast-summary-item">
          <span className="skeleton-block skeleton-label" />
          <span className="skeleton-block skeleton-value" />
        </div>
        <div className="forecast-summary-item">
          <span className="skeleton-block skeleton-label" />
          <span className="skeleton-block skeleton-value" />
        </div>
      </div>
      <div className="forecast-skeleton-chart" />
      <div className="forecast-labels">
        {Array.from({ length: 5 }).map((_, i) => (
          <span key={i} className="skeleton-block skeleton-daylabel" />
        ))}
      </div>
    </section>
  )
}

// ----- Wearable insights card -----
// Shown only when the user has an active wearable integration with at least one
// night of data. Three pillars:
//   1. "Live" pill with a pulsing dot + last-sync relative timestamp — this is
//      the main "we do wearables" signal the user should feel.
//   2. Four last-night metric tiles with count-up animation and a ▲/▼ chip vs
//      their 7-day baseline.
//   3. Two mini sparklines (HRV + RHR) with stroke-draw animation.

function useCountUp(target: number | null, durationMs = 900, decimals = 0): string {
  const [val, setVal] = useState(0)
  const rafRef = useRef<number | null>(null)
  useEffect(() => {
    if (target == null || Number.isNaN(target)) {
      setVal(0)
      return
    }
    if (typeof window === 'undefined' || typeof requestAnimationFrame === 'undefined') {
      setVal(target)
      return
    }
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setVal(target)
      return
    }
    const start = performance.now()
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs)
      const eased = 1 - Math.pow(1 - t, 3)
      setVal(target * eased)
      if (t < 1) rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [target, durationMs])
  if (target == null) return '—'
  return val.toFixed(decimals)
}

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return 'just now'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return 'just now'
  const diff = Math.max(0, Date.now() - then)
  const mins = Math.round(diff / 60_000)
  if (mins < 1) return 'moments ago'
  if (mins < 60) return `${mins} min ago`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.round(hrs / 24)
  return `${days}d ago`
}

function WearableInsightsCard({ data }: { data: WearableInsightsResponse }) {
  const { ref, revealed } = useRevealOnScroll<HTMLElement>()
  const ln = data.last_night
  const base = data.baseline_7d
  const series = data.series ?? []

  const sleepHours = ln?.sleep_total_minutes != null ? ln.sleep_total_minutes / 60 : null
  const sleepBaseHours = base?.sleep_total_minutes != null ? base.sleep_total_minutes / 60 : null

  const sleepStr = useCountUp(sleepHours, 900, 1)
  const hrvStr = useCountUp(ln?.hrv_rmssd_ms ?? null, 900, 0)
  const rhrStr = useCountUp(ln?.resting_hr_bpm ?? null, 900, 0)
  const readyStr = useCountUp(ln?.readiness_score ?? null, 900, 0)

  const tiles: {
    key: string
    label: string
    value: string
    unit?: string
    delta: { sign: 'up' | 'down' | 'flat'; text: string } | null
    accent: boolean
  }[] = [
    {
      key: 'sleep',
      label: 'Sleep',
      value: sleepHours != null ? sleepStr : '—',
      unit: 'h',
      delta: buildDelta(sleepHours, sleepBaseHours, 0.1, 'h'),
      accent: false,
    },
    {
      key: 'hrv',
      label: 'HRV',
      value: ln?.hrv_rmssd_ms != null ? hrvStr : '—',
      unit: 'ms',
      delta: buildDelta(ln?.hrv_rmssd_ms ?? null, base?.hrv_rmssd_ms ?? null, 1, ''),
      accent: true,
    },
    {
      key: 'rhr',
      label: 'Resting HR',
      value: ln?.resting_hr_bpm != null ? rhrStr : '—',
      unit: 'bpm',
      // Lower resting HR is better — flip the arrow sense.
      delta: invertDelta(buildDelta(ln?.resting_hr_bpm ?? null, base?.resting_hr_bpm ?? null, 1, '')),
      accent: false,
    },
    {
      key: 'readiness',
      label: 'Readiness',
      value: ln?.readiness_score != null ? readyStr : '—',
      unit: '/100',
      delta: buildDelta(ln?.readiness_score ?? null, base?.readiness_score ?? null, 2, ''),
      accent: false,
    },
  ]

  return (
    <section
      className={`card wearable-card${revealed ? ' is-revealed' : ''}`}
      ref={ref}
    >
      <div className="card-header wearable-header">
        <span className="card-title">{data.provider_display ?? 'Wearable'} insights</span>
        <span className="wearable-live-pill" title={`Last sync ${relativeTime(data.last_sync_at)}`}>
          <span className="wearable-live-dot" aria-hidden />
          Live · {data.provider_display ?? 'Oura Ring'}
        </span>
      </div>
      <div className="wearable-sync-row">
        <span className="wearable-sync-label">Last sync</span>
        <span className="wearable-sync-value">{relativeTime(data.last_sync_at)}</span>
        {(data.days_with_data ?? 0) > 0 && (
          <span className="wearable-sync-chip">{data.days_with_data}/14 days</span>
        )}
      </div>
      <div className="wearable-tiles">
        {tiles.map((t, i) => (
          <div
            key={t.key}
            className={`wearable-tile${t.accent ? ' wearable-tile-accent' : ''}`}
            style={{ ['--wearable-tile-delay' as string]: `${80 + i * 70}ms` }}
          >
            <div className="wearable-tile-label">{t.label}</div>
            <div className="wearable-tile-value-row">
              <span className="wearable-tile-value">{t.value}</span>
              {t.unit && <span className="wearable-tile-unit">{t.unit}</span>}
            </div>
            {t.delta ? (
              <div className={`wearable-tile-delta wearable-delta-${t.delta.sign}`}>
                <span className="wearable-delta-arrow" aria-hidden>
                  {t.delta.sign === 'up' ? '▲' : t.delta.sign === 'down' ? '▼' : '•'}
                </span>
                {t.delta.text}
              </div>
            ) : (
              <div className="wearable-tile-delta wearable-delta-flat">vs 7-day</div>
            )}
          </div>
        ))}
      </div>
      <div className="wearable-sparklines">
        <WearableSparkline
          label="HRV · 14d"
          series={series}
          accessor={(p) => p.hrv}
          color="var(--accent, #6e3bff)"
          revealed={revealed}
          delayMs={360}
        />
        <WearableSparkline
          label="Resting HR · 14d"
          series={series}
          accessor={(p) => p.rhr}
          color="#c94060"
          revealed={revealed}
          delayMs={460}
        />
      </div>
      {data.signal && (
        <div className="wearable-signal">
          <span className="wearable-signal-icon" aria-hidden>✦</span>
          {data.signal}
        </div>
      )}
    </section>
  )
}

function buildDelta(
  current: number | null,
  baseline: number | null,
  epsilon: number,
  unit: string,
): { sign: 'up' | 'down' | 'flat'; text: string } | null {
  if (current == null || baseline == null || baseline === 0) return null
  const diff = current - baseline
  if (Math.abs(diff) < epsilon) return { sign: 'flat', text: 'near 7-day avg' }
  const pct = Math.round((diff / baseline) * 100)
  const sign: 'up' | 'down' = diff > 0 ? 'up' : 'down'
  const magnitude = Math.abs(pct)
  return {
    sign,
    text: `${magnitude}%${unit ? ' ' + unit : ''} vs 7-day`,
  }
}

function invertDelta(
  d: { sign: 'up' | 'down' | 'flat'; text: string } | null,
): { sign: 'up' | 'down' | 'flat'; text: string } | null {
  // For resting HR, down is good — swap the semantic sign so the green/red
  // chips reflect the health direction, not the numeric direction.
  if (!d) return d
  if (d.sign === 'flat') return d
  return { sign: d.sign === 'up' ? 'down' : 'up', text: d.text }
}

function WearableSparkline({
  label,
  series,
  accessor,
  color,
  revealed,
  delayMs,
}: {
  label: string
  series: WearableSeriesPoint[]
  accessor: (p: WearableSeriesPoint) => number | null
  color: string
  revealed: boolean
  delayMs: number
}) {
  const W = 180
  const H = 44
  const PAD_X = 4
  const PAD_Y = 6
  const vals = series.map(accessor)
  const present = vals.filter((v): v is number => v != null)
  if (present.length < 2) {
    return (
      <div className="wearable-spark">
        <div className="wearable-spark-label">{label}</div>
        <div className="wearable-spark-empty">Needs more nights</div>
      </div>
    )
  }
  const min = Math.min(...present)
  const max = Math.max(...present)
  const range = max - min || 1
  const plotW = W - PAD_X * 2
  const plotH = H - PAD_Y * 2
  const xAt = (i: number) => PAD_X + (i / Math.max(1, series.length - 1)) * plotW
  const yAt = (v: number) => PAD_Y + plotH - ((v - min) / range) * plotH

  const pts = series
    .map((p, i) => {
      const v = accessor(p)
      return v == null ? null : { x: xAt(i), y: yAt(v), v, i }
    })
    .filter((p): p is { x: number; y: number; v: number; i: number } => p !== null)

  const pathD = buildSmoothPath(pts.map((p) => ({ x: p.x, y: p.y })))
  const last = pts[pts.length - 1]
  const first = pts[0]
  const trend = last.v >= first.v ? 'up' : 'down'

  return (
    <div className={`wearable-spark wearable-spark-${trend}`}>
      <div className="wearable-spark-head">
        <span className="wearable-spark-label">{label}</span>
        <span className="wearable-spark-now" style={{ color }}>
          {last.v.toFixed(0)}
        </span>
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className={`wearable-spark-svg${revealed ? ' is-revealed' : ''}`}
        style={{ ['--spark-delay' as string]: `${delayMs}ms` }}
        role="img"
        aria-label={`${label} trend`}
      >
        <defs>
          <linearGradient id={`spark-fill-${label}`} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.25" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        {pathD && (
          <>
            <path
              d={`${pathD} L ${last.x} ${H - PAD_Y} L ${first.x} ${H - PAD_Y} Z`}
              fill={`url(#spark-fill-${label})`}
              className="wearable-spark-area"
            />
            <path
              d={pathD}
              fill="none"
              stroke={color}
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
              className="wearable-spark-line"
            />
            <circle
              cx={last.x}
              cy={last.y}
              r="2.6"
              fill={color}
              className="wearable-spark-dot"
            />
          </>
        )}
      </svg>
    </div>
  )
}

function CycleCardSkeleton() {
  return (
    <section className="card cycle-predictor-card cycle-card-skeleton" aria-busy="true" aria-label="Loading cycle predictor">
      <div className="card-header">
        <span className="skeleton-block skeleton-title" />
        <span className="skeleton-block skeleton-pill" />
      </div>
      <div className="cycle-metrics">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="cycle-metric-card">
            <span className="skeleton-block skeleton-metric-label" />
            <span className="skeleton-block skeleton-metric-value" />
            <span className="skeleton-block skeleton-metric-sub" />
          </div>
        ))}
      </div>
      <div className="cycle-legend">
        {Array.from({ length: 4 }).map((_, i) => (
          <span key={i} className="skeleton-block skeleton-legend" />
        ))}
      </div>
      <div className="cycle-skeleton-chart" />
    </section>
  )
}

function CycleInsightsGrid({
  insights,
}: Readonly<{
  insights: NonNullable<ReturnType<typeof useCyclePredictor>['data']>['insights']
}>) {
  const { ref, revealed } = useRevealOnScroll<HTMLDivElement>()
  if (!insights) return null
  return (
    <div className="cycle-insights" ref={ref}>
      <div className="cycle-insights-title">What to expect</div>
      <div className={`cycle-insights-grid${revealed ? ' is-revealed' : ''}`}>
        {insights.map((i, idx) => {
          const color = PHASE_COLOR[i.phase] ?? 'var(--accent)'
          return (
            <div
              key={`${i.phase}-${idx}`}
              className="cycle-insight-card"
              style={{
                ['--insight-color' as string]: color,
                ['--insight-delay' as string]: `${idx * 70}ms`,
              }}
            >
              <div className="cycle-insight-phase-row">
                <span className="cycle-insight-dot" />
                <span className="cycle-insight-phase">{i.title}</span>
              </div>
              <div className="cycle-insight-body">{i.body}</div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ----- Condition flags -----
// Surfaces conditions whose pattern the server has detected in the user's
// intake + logged data. Intentionally framed as "Pelvia noticed" with a
// disclaimer — this is not a diagnosis, it's signal to discuss with a doctor.

const CONDITION_BADGE: Record<ConditionFlag['confidence'], string> = {
  confirmed: 'Confirmed',
  high: 'High likelihood',
  moderate: 'Moderate',
  low: 'Possible',
}
const CONDITION_TIER: Record<number, string> = {
  1: 'From intake',
  2: '30+ days tracked',
  3: '60+ days tracked',
}

function ConditionFlagsCard({ flags }: { flags: ConditionFlag[] }) {
  if (!flags || flags.length === 0) return null
  return (
    <section className="card condition-flags-card">
      <div className="card-header">
        <span className="card-title">Pelvia noticed</span>
      </div>
      <div className="condition-flags-list">
        {flags.map((f, i) => (
          <ConditionFlagItem key={`${f.condition}-${i}`} flag={f} />
        ))}
      </div>
      <div className="condition-flag-disclaimer">
        Not a diagnosis — patterns Pelvia noticed in your data. Discuss with your
        doctor.
      </div>
    </section>
  )
}

function ConditionFlagItem({ flag }: { flag: ConditionFlag }) {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const criteria = flag.criteria_met ?? []
  const supporting = flag.supporting_signals ?? []
  const hasDetails = criteria.length > 0 || supporting.length > 0
  const tierText = flag.tier != null ? CONDITION_TIER[flag.tier] : ''
  // Whole card is clickable — sends the user into the coach with a seeded
  // "what should I discuss with my doctor" message. Inner buttons stop
  // propagation so toggling details doesn't also jump to the coach.
  const goDiscuss = () => {
    navigate('/patient/coach', {
      state: { seedPrompt: coachSeedForCondition(flag.condition, flag.message) },
    })
  }
  return (
    <div
      className={`condition-flag-card confidence-${flag.confidence} is-clickable`}
      role="button"
      tabIndex={0}
      onClick={goDiscuss}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          goDiscuss()
        }
      }}
    >
      <div className="condition-flag-header">
        <span className="condition-flag-name">{flag.condition}</span>
        <span className="condition-flag-badge">
          {CONDITION_BADGE[flag.confidence] ?? flag.confidence}
        </span>
        {tierText && (
          <span className="condition-flag-tier">
            Tier {flag.tier} · {tierText}
          </span>
        )}
      </div>
      <div className="condition-flag-message">{flag.message}</div>
      <div className="condition-flag-cta" aria-hidden>
        Discuss with coach →
      </div>
      {hasDetails && (
        <>
          <button
            type="button"
            className="condition-flag-toggle"
            onClick={(e) => {
              e.stopPropagation()
              setOpen((o) => !o)
            }}
          >
            {open ? 'Hide details' : 'View details'}
          </button>
          {open && (
            <div className="condition-flag-details">
              {criteria.length > 0 && (
                <div className="condition-flag-criteria">
                  {criteria.map((c) => (
                    <span key={c} className="condition-flag-chip">
                      {c}
                    </span>
                  ))}
                </div>
              )}
              {supporting.length > 0 && (
                <div
                  className="condition-flag-criteria"
                  style={{ marginTop: '0.35rem' }}
                >
                  {supporting.map((s) => (
                    <span key={s} className="condition-flag-chip">
                      {s}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ----- Patterns card -----

function PatternsCard({
  patterns,
  unlocked,
  daysLogged,
}: {
  patterns: PainPattern[]
  unlocked: boolean
  daysLogged: number
}) {
  if (!unlocked) {
    const needed = Math.max(0, 7 - daysLogged)
    return (
      <section className="card patterns-card-locked">
        <div className="card-header">
          <span className="card-title">Your patterns</span>
        </div>
        <p className="patterns-locked-copy">
          <strong>{daysLogged} of 7 logs</strong>
          {needed > 0
            ? ` — ${needed} more ${needed === 1 ? 'day' : 'days'} to unlock your first insights.`
            : ' — your first pattern report is unlocking now.'}
        </p>
        <div className="patterns-progress">
          <div
            className="patterns-progress-fill"
            style={{ width: `${Math.min(100, (daysLogged / 7) * 100)}%` }}
          />
        </div>
        <ul className="patterns-unlocks-list">
          <li>Pain trend chart</li>
          <li>Trigger patterns (sleep, stress, food)</li>
          <li>Cycle correlation</li>
        </ul>
      </section>
    )
  }

  if (patterns.length === 0) {
    return (
      <section className="card">
        <div className="card-header">
          <span className="card-title">Your patterns</span>
        </div>
        <p className="patterns-empty">
          We haven't spotted a clear pattern yet — keep logging and we'll surface one
          as soon as the signal is strong enough.
        </p>
      </section>
    )
  }

  return (
    <section className="card">
      <div className="card-header">
        <span className="card-title">Your patterns</span>
      </div>
      <p className="pp-card-desc">
        Patterns in your data worth knowing. Not a diagnosis — tap one to discuss with
        your AI coach.
      </p>
      <div className="insight-list">
        {patterns.map((p, i) => (
          <InsightCard key={p.id ?? `${p.type}-${i}`} pattern={p} index={i} />
        ))}
      </div>
    </section>
  )
}

function PatternsCardSkeleton() {
  return (
    <section className="card patterns-card-skeleton" aria-busy="true" aria-label="Loading your patterns">
      <div className="card-header">
        <span className="skeleton-block skeleton-title" />
      </div>
      <div className="insight-list">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="insight-skeleton">
            <span className="skeleton-block skeleton-chip" />
            <span className="skeleton-block skeleton-line-title" />
            <span className="skeleton-block skeleton-line" />
            <span className="skeleton-block skeleton-line short" />
          </div>
        ))}
      </div>
    </section>
  )
}

// ----- Estimate info modal -----
// Explains what an "estimated" dashed point on the chart means.
function EstimateInfoModal({ onClose }: { onClose: () => void }) {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
        <h3>How estimates work</h3>
        <p>
          For days you didn't log, Pelvia estimates your pain from your Oura biometrics —
          heart-rate variability, resting heart rate, sleep fragmentation, and body-temperature
          deviation. Each signal is compared against your own 30-day baseline.
        </p>
        <p>
          Estimates are shown with a dashed marker and a confidence score. Low-confidence
          estimates are excluded from clinical summaries. Your own logs always override the
          estimate for the same day.
        </p>
        <div className="modal-actions">
          <button type="button" className="btn btn-primary" onClick={onClose}>Got it</button>
        </div>
      </div>
    </div>
  )
}

// ----- Wearable skipper banner -----
// Nudges users who haven't connected a wearable to link one. Dismissing it once
// hides it for good; it also auto-hides after a few views so users without a
// wearable aren't nagged forever.
function WearableSkipperBanner() {
  const { data: status, isLoading, isFetching } = useWearableStatus()
  const connect = useConnectWearable()
  const hasStatus = !!status
  const connected = (status?.integrations || []).some(
    (i) => i.status === 'active' || i.status === 'syncing',
  )
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(WEARABLE_BANNER_DISMISSED_KEY) === '1'
    } catch {
      return false
    }
  })
  const [views] = useState<number>(() => {
    try {
      return parseInt(localStorage.getItem(WEARABLE_BANNER_VIEWS_KEY) || '0', 10) || 0
    } catch {
      return 0
    }
  })

  // Don't render until the status query resolves, otherwise connected users
  // see a brief "Connect Oura" flash on every refresh.
  const willHide = !hasStatus || isLoading || isFetching || connected
    || dismissed || views >= WEARABLE_BANNER_MAX_VIEWS

  // Count this as a view only when the banner is actually shown.
  useEffect(() => {
    if (willHide) return
    try {
      localStorage.setItem(WEARABLE_BANNER_VIEWS_KEY, String(views + 1))
    } catch { /* quota */ }
  }, [willHide, views])

  if (willHide) return null

  const dismiss = () => {
    try { localStorage.setItem(WEARABLE_BANNER_DISMISSED_KEY, '1') } catch { /* quota */ }
    setDismissed(true)
  }

  return (
    <div className="wearable-banner" role="status">
      <span className="wearable-banner-icon" aria-hidden>
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8">
          <circle cx="12" cy="12" r="8" />
          <circle cx="12" cy="12" r="4" />
        </svg>
      </span>
      <span className="wearable-banner-text">
        Connect Oura for auto-tracking — sleep, HRV, and pain-score estimates.
      </span>
      <button
        type="button"
        className="wearable-banner-cta"
        onClick={() => connect.mutate('oura')}
        disabled={connect.isPending}
      >
        {connect.isPending ? 'Redirecting…' : 'Connect'}
      </button>
      <button
        type="button"
        className="wearable-banner-dismiss"
        onClick={dismiss}
        aria-label="Dismiss"
      >
        ×
      </button>
    </div>
  )
}

